// ZonePeek — the zone peek body, rendered by the global PeekHost from
// `?peek=zones:<id>` (ADR-036 §2), on THE platform peek grammar: a Contents
// section (the read-only StatStrip) and — when the field-force module is on — a
// Responsibilities section. The acts come from the zone's ONE registry
// (`useZoneActions`): the top row = Edit zone (the primary — the one edit, a quiet
// button); the Responsibilities card carries its own act, "Manage" (a zone is
// PAGELESS — ADR-051: its sectioned edit lives on its card, never in the Edit
// menu — ADR-036 §7, exception 1; a namer outside the zone's own scope sees the
// link disabled, "Outside your scope" under it); the footer = the labelled
// Actions door (Set as default · the lifecycle). No "Open full page".

import { useTranslation } from 'react-i18next';
import type { ZoneDetail } from '@ems-app/schemas/zones/responses';
import { useZone } from '../../../hooks/useZones.js';
import { useEntitlements } from '../../../hooks/useEntitlements.js';
import { ContextPanel } from '../../../components/patterns/ContextPanel.js';
import { DetailSection } from '../../../components/patterns/DetailSection.js';
import { SectionAct } from '../../../components/patterns/SectionAct.js';
import { StatStrip } from '../../../components/patterns/StatStrip.js';
import { ScopeChainsSection } from '../../../components/patterns/ScopeChainsSection.js';
import { useEntityPeek } from '../../../hooks/useEntityPeek.js';
import { Alert } from '../../../components/ui/Alert.js';
import { PageLoader } from '../../../components/ui/PageLoader.js';
import { icons } from '../../../components/ui/icons.js';
import { formatNumber } from '../../../lib/format.js';
import { ZoneStatusGroup } from './zoneDisplay.js';
import { useZoneActions } from './ZoneActions.js';

export interface ZonePeekProps {
  projectId: string;
  zoneId: number;
  onClose: () => void;
}

export function ZonePeek({ projectId, zoneId, onClose }: ZonePeekProps) {
  const { t } = useTranslation('zones');
  const zoneQ = useZone(projectId, zoneId);

  if (zoneQ.isPending) {
    return (
      <ContextPanel host="peek" onClose={onClose} title={t('list.title')}>
        <div className="flex min-h-[8rem] items-center justify-center">
          <PageLoader />
        </div>
      </ContextPanel>
    );
  }
  if (zoneQ.isError || !zoneQ.data) {
    return (
      <ContextPanel host="peek" onClose={onClose} title={t('detail.error')}>
        <Alert variant="danger">{t('detail.error')}</Alert>
      </ContextPanel>
    );
  }
  return <ZonePeekBody projectId={projectId} zone={zoneQ.data} onClose={onClose} />;
}

// The loaded peek — its own component so the acts hook runs on a zone that is there.
function ZonePeekBody({ projectId, zone: z, onClose }: { projectId: string; zone: ZoneDetail; onClose: () => void }) {
  const { t } = useTranslation('zones');
  const hasFieldForce = useEntitlements(projectId).has('has_field_force');
  const { acts, overlays } = useZoneActions(projectId, z, { onGone: onClose });
  // A person on a chain is followed inside the sheet — the zone joins the way back.
  const { followPeek } = useEntityPeek();
  const peekPerson = (userId: string) => followPeek(`users:${userId}`, z.name);
  const stats = [
    { label: t('detail.contents.sites'), value: formatNumber(z.site_count) },
    { label: t('detail.contents.warehouses'), value: formatNumber(z.warehouse_count) },
    { label: t('detail.contents.vehiclePools'), value: formatNumber(z.vehicle_pool_count) },
  ];

  return (
    <>
      <ContextPanel
        host="peek"
        onClose={onClose}
        leading={<icons.zone size={22} aria-hidden className="text-[var(--color-text-secondary)]" />}
        title={z.name}
        kind={t('detail.kind')}
        status={<ZoneStatusGroup isDisabled={z.is_disabled} isDefault={z.is_default} t={t} />}
        acts={acts}
        pageless
      >
        <DetailSection dense title={t('detail.sections.contents')}>
          {/* Unframed StatStrip — the section card IS the frame (DESIGN-TOKENS §StatStrip). */}
          <StatStrip stats={stats} />
        </DetailSection>
        {hasFieldForce && (
          <DetailSection dense title={t('detail.sections.responsibilities')} action={<SectionAct acts={acts} section="responsibilities" />}>
            <ScopeChainsSection projectId={projectId} scope={{ level: 'zone', id: z.id }} peekPerson={peekPerson} />
          </DetailSection>
        )}
      </ContextPanel>
      {overlays}
    </>
  );
}
